import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import useApi from 'hooks/useApi';

import { ReactComponent as TextFieldIcon } from 'assets/icon-text-field.svg';
import { ReactComponent as EyeIcon } from 'assets/icon-visible.svg';

import AlertModal from 'shared/components/AlertModal';
import SectionHeadline from 'shared/components/SectionHeadline';
import { LabelRequiredInput, LabelError } from 'shared/components/Labels';
import CustomInput from 'shared/components/CustomInput';
import { Switch } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import { ImageUploader } from 'shared/components/FileUploaders';
import { toBase64 } from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import RadioButton from 'shared/components/RadioButton';
import { DEFAULT_SMTP } from 'shared/constants/contants';

const IconContainer = styled.div`
  height: 24px;
  width: 24px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
  padding-left: 4px;
  padding-right: 4px;
  svg {
    height: 100%;
    width: 100%;
  }
  margin-top: 4px;
`;

const SendEmailSettingsSection = ({
  mainColor,
  selectedNotificationImg,
  setSelectedNotificationImg,
  server,
  isGenericAccount,
  setIsGenericAccount,
  isGenericAccountSet,
  userGeneric,
  setUserGeneric,
  setServer,
  port,
  setPort,
  user,
  setUser,
  pass,
  setPass,
  safeConnection,
  setSafeConnection,
  TLS,
  setTLS,
}) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const [uploadedNotificationImgError, setUploadedNotificationImgError] = useState('');
  const [showSuccess, setShowSuccess] = useState();
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [, smtpCheck] = customAreaApi(
    {
      url: `${window.API_V1}/login/smtp/check`,
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onToggleSuccess = () => setShowSuccess((currentShow) => !currentShow);

  const onOkSuccess = () => {
    onToggleSuccess();
  };

  const onChangeSafeConnection = () => {
    if (!safeConnection && TLS) {
      setTLS(false);
      setSafeConnection(true);
    } else {
      setSafeConnection(!safeConnection);
    }
  };

  const onChangeTLS = () => {
    if (!TLS && safeConnection) {
      setSafeConnection(false);
      setTLS(true);
    } else {
      setTLS(!TLS);
    }
  };

  const onChangeNotificationImg = async (file) => {
    const curretFileBase64 = await toBase64(file);
    setSelectedNotificationImg(curretFileBase64);
  };

  const onSaveNotificationImgBase64 = async (base64) => {
    setSelectedNotificationImg(base64);
  };

  const onErrorNotificationImgUploading = (error) => {
    setUploadedNotificationImgError(error);
  };

  const onChange = (event, set) => {
    set(event.target.value);
  };

  const onSmtpCheck = async () => {
    try {
      const newData = {
        account: user,
        pass,
        port,
        server,
        ssl: safeConnection,
        tls: TLS,
        user,
      };
      const {
        data: { data },
      } = await smtpCheck({ data: newData });
      if (!data) {
        throw new Error(
          data.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      setShowSuccess(true);
    } catch (error) {
      setShowError(true);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const smtpOptions = [
    {
      name: t('general_customizer_label_default_connection'),
      value: true,
      tooltipLabel: t('general_customizer_label_default_connection_help'),
    },
    {
      name: t('general_customizer_label_custom_connection'),
      value: false,
      tooltipLabel: t('general_customizer_label_custom_connection_help'),
    },
  ];

  return (
    <>
      <SectionHeadline
        className="general-customizer__section-headline-3"
        sectionNumber={4}
        label={t('general_customizer_section_send_email_settings')}
        color={mainColor}
      />
      <ImageUploader
        className="general-customizer__logo-uploader"
        mainColor={mainColor}
        label={t('general_customizer_label_notification_img')}
        labelClassName="text-uppercase"
        labelColor={mainColor}
        required
        imageBase64={selectedNotificationImg}
        onChangeFile={onChangeNotificationImg}
        onSaveBase64={onSaveNotificationImgBase64}
        onError={onErrorNotificationImgUploading}
      />
      {uploadedNotificationImgError && <LabelError label={uploadedNotificationImgError} />}
      <div className="general-customizer__settings-smtp-container">
        <div>
          <LabelRequiredInput
            color={mainColor}
            label={t('general_customizer_label_connection_data')}
            className="image-uploader__label text-uppercase"
          />
        </div>
        <div className="general-customizer__settings-smtp">
          <div className="py-4">
            <ul className="list-unstyled d-flex gap-3">
              {smtpOptions.map((option) => (
                <li
                  key={option.value}
                  className="d-flex align-items-center list-client__text mr-5 py-2"
                >
                  <RadioButton
                    option={option}
                    selectedOption={isGenericAccount}
                    onClick={setIsGenericAccount}
                    tooltipLabel={option.tooltipLabel}
                  />
                </li>
              ))}
            </ul>
          </div>
          <div className="general-customizer__settings-smtp-wrapper">
            <div className="input-wrapper">
              <LabelRequiredInput
                className="text-uppercase"
                label={t('general_outbound_server')}
                color={mainColor}
              />
              <CustomInput
                type="url"
                Icon={() => (
                  <IconContainer backgroundColor="transparent">
                    <TextFieldIcon fill={colors.suvaGrey} />
                  </IconContainer>
                )}
                value={isGenericAccount ? '' : server}
                onChange={(event) => onChange(event, setServer)}
                disabled={isGenericAccount}
              />
            </div>
            <div className="input-wrapper">
              <LabelRequiredInput
                className="text-uppercase"
                label={t('general_port')}
                color={mainColor}
              />
              <CustomInput
                type="url"
                Icon={() => (
                  <IconContainer backgroundColor="transparent">
                    <TextFieldIcon fill={colors.suvaGrey} />
                  </IconContainer>
                )}
                value={isGenericAccount ? '' : port}
                onChange={(event) => onChange(event, setPort)}
                disabled={isGenericAccount}
              />
            </div>
            <div className="input-wrapper">
              <LabelRequiredInput
                className="text-uppercase"
                label={t('login_username')}
                color={mainColor}
              />
              <CustomInput
                type="url"
                endAddonText={isGenericAccount ? `@${DEFAULT_SMTP.host}` : ''}
                placeholder={t('login_username_placeholder')}
                Icon={() => (
                  <IconContainer backgroundColor="transparent">
                    <TextFieldIcon fill={colors.suvaGrey} />
                  </IconContainer>
                )}
                value={isGenericAccount ? userGeneric : user}
                onChange={(event) => onChange(event, isGenericAccount ? setUserGeneric : setUser)}
                disabled={isGenericAccount && isGenericAccountSet}
              />
            </div>
            <div className="input-wrapper">
              <LabelRequiredInput
                className="text-uppercase"
                label={t('login_password')}
                color={mainColor}
              />
              <CustomInput
                type={showPassword ? 'text' : 'password'}
                Icon={() => (
                  <IconContainer backgroundColor="transparent" onClick={toggleShowPassword}>
                    <EyeIcon fill={colors.suvaGrey} />
                  </IconContainer>
                )}
                value={isGenericAccount ? '' : pass}
                onChange={(event) => onChange(event, setPass)}
                disabled={isGenericAccount}
              />
            </div>
          </div>
          <div className="general-customizer__connection_wrapper">
            <div className="general-customizer__connection_wrapper-switch">
              <Switch
                className="text-uppercase"
                id="switchSafeConnection"
                label={t('general_ssl')}
                color={safeConnection ? mainColor : colors.pattens}
                textColor={mainColor}
                value={!safeConnection}
                onChange={onChangeSafeConnection}
                disabled={isGenericAccount}
              />
              <Switch
                className="text-uppercase"
                id="switchTLS"
                label={t('general_tls')}
                color={TLS ? mainColor : colors.pattens}
                textColor={mainColor}
                value={!TLS}
                onChange={onChangeTLS}
                disabled={isGenericAccount}
              />
            </div>
            <Button
              className="general-customizer__test-connection-button"
              label={t('general_test_connection')}
              color="primary"
              type="button"
              onClick={() => onSmtpCheck()}
              backgroundColor={mainColor}
              disabled={isGenericAccount}
            />
          </div>
        </div>
      </div>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('general_smtp_connection_error')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <AlertModal
        type="success"
        isOpen={showSuccess}
        toggle={onOkSuccess}
        message={t('general_smtp_connection_success')}
        okText={t('general_button_ok')}
        onOk={onOkSuccess}
      />
    </>
  );
};

SendEmailSettingsSection.defaultProps = {
  mainColor: '',
  selectedNotificationImg: undefined,
  setSelectedNotificationImg: undefined,
};

SendEmailSettingsSection.propTypes = {
  mainColor: PropTypes.string,
  selectedNotificationImg: PropTypes.string,
  setSelectedNotificationImg: PropTypes.func,
  isGenericAccount: PropTypes.bool.isRequired,
  setIsGenericAccount: PropTypes.func.isRequired,
  isGenericAccountSet: PropTypes.bool.isRequired,
  userGeneric: PropTypes.string.isRequired,
  setUserGeneric: PropTypes.func.isRequired,
  server: PropTypes.string.isRequired,
  setServer: PropTypes.func.isRequired,
  port: PropTypes.string.isRequired,
  setPort: PropTypes.func.isRequired,
  user: PropTypes.string.isRequired,
  setUser: PropTypes.func.isRequired,
  pass: PropTypes.string.isRequired,
  setPass: PropTypes.func.isRequired,
  safeConnection: PropTypes.bool.isRequired,
  setSafeConnection: PropTypes.func.isRequired,
  TLS: PropTypes.bool.isRequired,
  setTLS: PropTypes.func.isRequired,
};

export default SendEmailSettingsSection;
